import { ChangeEvent, useCallback, useEffect, useState } from "react";

import { CampaignFinderProps } from "./type";

import { trpc } from "@/common/helpers/trpc";
import type { DonationFormFromCampaignOutput } from "@/common/types/trpc";

export const useCampaignFinder = ({ campaignId }: CampaignFinderProps) => {
  const utils = trpc.useUtils();
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [hasMoreData, setHasMoreData] = useState(true);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [refetchOrFetchMoreLoading, setRefetchOrFetchMoreLoading] = useState(false);
  const [individualForms, setIndividualForms] = useState<DonationFormFromCampaignOutput[]>([]);
  const [teamForms, setTeamForms] = useState<DonationFormFromCampaignOutput[]>([]);

  const fetchForms = useCallback(
    async ({ page: pageToFetch, search: searchTerm, append }: { page: number; search: string; append: boolean }) => {
      if (append) {
        setRefetchOrFetchMoreLoading(true);
      } else {
        setIsInitialLoading(true);
        setRefetchOrFetchMoreLoading(false);
      }

      try {
        const data = await utils.getDonationFormsFromCampaign.fetch({
          campaignId,
          filters: {
            page: pageToFetch,
            search: searchTerm,
          },
        });

        const normalizeDonationForm = (form: DonationFormFromCampaignOutput): DonationFormFromCampaignOutput => ({
          ...form,
          donationFormFields: form.donationFormFields ?? null,
          path: form.path ?? null,
        });

        const normalizedIndividual = data.individual.map(normalizeDonationForm);
        const normalizedTeam = data.team.map(normalizeDonationForm);

        setIndividualForms((prev) => (append ? [...prev, ...normalizedIndividual] : normalizedIndividual));
        setTeamForms((prev) => (append ? [...prev, ...normalizedTeam] : normalizedTeam));
        setHasMoreData(data.hasMore);
        setPage(pageToFetch);
      } finally {
        if (append) {
          setRefetchOrFetchMoreLoading(false);
        } else {
          setIsInitialLoading(false);
        }
      }
    },
    [campaignId, utils]
  );

  const loadMore = useCallback(() => {
    if (!hasMoreData) {
      return;
    }
    void fetchForms({ page: page + 1, search, append: true });
  }, [fetchForms, hasMoreData, page, search]);

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const nextSearch = event.target.value;
      setSearch(nextSearch);
      void fetchForms({ page: 1, search: nextSearch, append: false });
    },
    [fetchForms]
  );

  // Initial load
  useEffect(() => {
    void fetchForms({ page: 1, search: "", append: false });
  }, [fetchForms]);

  return {
    teamDonationForms: teamForms,
    fundraiserDonationForms: individualForms,
    handleChange,
    loading: isInitialLoading,
    refetchOrFetchMoreLoading,
    loadMore,
    search,
    hasMoreData,
  };
};
